import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

/** Shared collapsible panel wrapper — unifies all dashboard modules to match the
 * "Започнете тук" (OnboardingChecklist) interaction pattern.
 *
 * Props:
 *   - title: required
 *   - icon: lucide icon component (optional)
 *   - iconColor: hex string (default #6366F1)
 *   - credits: number — shows "⚡ N credits" pill on the header
 *   - subtitle / badge: optional extra info
 *   - defaultOpen: boolean, default true
 *   - testId: string — wraps the whole card
 *   - children: body (rendered when expanded)
 */
export default function CollapsiblePanel({
  title,
  icon: Icon,
  iconColor = "#6366F1",
  credits,
  subtitle,
  badge,
  defaultOpen = true,
  storageKey,
  testId = "collapsible-panel",
  className = "",
  openSignal,  // Incrementing number — when it changes, force-expand the panel
  children,
}) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const persistKey = storageKey ? `panel-open:${storageKey}` : null;
  const initial = React.useMemo(() => {
    if (!persistKey) return defaultOpen;
    try {
      const v = localStorage.getItem(persistKey);
      if (v === null) return defaultOpen;
      return v === "true";
    } catch {
      return defaultOpen;
    }
  }, [persistKey, defaultOpen]);

  const [open, setOpen] = React.useState(initial);

  // External force-expand trigger (e.g., user clicked a backfill day)
  React.useEffect(() => {
    if (openSignal !== undefined && openSignal !== null) {
      setOpen(true);
      if (persistKey) { try { localStorage.setItem(persistKey, "true"); } catch { /* noop */ } }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (persistKey) {
      try { localStorage.setItem(persistKey, String(next)); } catch {}
    }
  };

  return (
    <div
      id={testId}
      className={`rounded-2xl border ${isDark ? "bg-card border-border" : "bg-white card-elevated border-0"} ${className}`}
      data-testid={testId}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}
        className={`w-full flex items-start sm:items-center justify-between gap-2 px-3 sm:px-4 py-3 rounded-2xl transition-colors cursor-pointer select-none ${
          open ? "rounded-b-none" : ""
        } ${isDark ? "hover:bg-[hsl(223,47%,14%)]" : "hover:bg-slate-50"}`}
        data-testid={`${testId}-toggle`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0 flex-1">
          {Icon && (
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${iconColor}, ${iconColor}cc)` }}
            >
              <Icon className="w-4 h-4 text-white" />
            </div>
          )}
          <div className="text-left min-w-0 flex-1">
            <h3 className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {title}
            </h3>
            {subtitle && (
              <p className={`text-[11px] leading-snug line-clamp-2 sm:truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 flex-wrap justify-end max-w-[45%] sm:max-w-none">
          {badge}
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {open && (
        <div className="px-4 pb-4 pt-1" data-testid={`${testId}-content`}>
          {children}
        </div>
      )}
    </div>
  );
}
